// Слепое сравнение роликов: пара за парой, стороны перемешаны, какой ролик новый — не видно, пока не решены
// все пары. Итог — «новые не хуже прежних в N из M» (критерий этапа 4 — не хуже в ≥4 из 6).
import { Button, Card, Chip, EmptyState, Skeleton, toast } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Equal, Scale } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import { PageHead } from '../common';
import { CreativeTabs } from './CreativeTabs';
import { mediaUrl } from './data';
import './creatives.css';

type Pick = 'left' | 'right' | 'tie';

interface Clip {
  video: string | null;
  poster: string | null;
  id?: number;
  name?: string;
}

interface Comparison {
  id: number;
  title: string;
  pairs: Array<{ id: number; position: number; left: Clip; right: Clip; pick: Pick | null; newSide?: Pick }>;
  result: { total: number; newWins: number; ties: number; refWins: number; notWorse: number } | null;
}

const PICK_RU: Record<Pick, string> = { left: 'левый', right: 'правый', tie: 'одинаково' };

function Player({ clip, label }: { clip: Clip; label: string }) {
  return (
    <figure className="cmp-clip">
      {/* biome-ignore lint/a11y/useMediaCaption: субтитры вшиты в сам ролик (караоке) */}
      <video
        src={mediaUrl(clip.video) ?? undefined}
        poster={mediaUrl(clip.poster) ?? undefined}
        controls
        playsInline
        preload="metadata"
        aria-label={label}
      />
      <figcaption>{label}</figcaption>
    </figure>
  );
}

export function ComparePage() {
  const { id } = useParams({ from: '/creatives/compare/$id' });
  const qc = useQueryClient();
  const key = useMemo(() => ['compare', id], [id]);
  const q = useQuery({ queryKey: key, queryFn: () => api<Comparison>(`/api/compare/${id}`) });
  const [busy, setBusy] = useState(false);
  const d = q.data;
  const current = d?.pairs.find((p) => !p.pick) ?? null;
  const decided = d?.pairs.filter((p) => p.pick).length ?? 0;

  const choose = useCallback(
    async (pick: Pick) => {
      if (!current || busy) return;
      setBusy(true);
      try {
        qc.setQueryData(key, await api<Comparison>(`/api/compare/${id}/pairs/${current.id}`, { pick }));
      } catch (e) {
        toast((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [current, busy, id, qc, key],
  );

  // Стрелки клавиатуры: ← левый, → правый, = или ↓ — одинаково
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement | null)?.closest('input,textarea,video')) return;
      if (e.key === 'ArrowLeft') void choose('left');
      else if (e.key === 'ArrowRight') void choose('right');
      else if (e.key === '=' || e.key === 'ArrowDown') void choose('tie');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [choose]);

  return (
    <>
      <PageHead
        title="Слепое сравнение"
        sub={d?.title ?? 'Какой ролик лучше — не зная, какой из них новый'}
        actions={
          <Link to="/creatives" className="btn">
            <ArrowLeft /> К пулу
          </Link>
        }
      />
      <CreativeTabs active="/creatives" />
      <div className="pc">
        {q.isPending ? (
          <div className="cmp-pair">
            <Skeleton className="cmp-skel" h={0} />
            <Skeleton className="cmp-skel" h={0} />
          </div>
        ) : q.isError || !d ? (
          <div className="card">
            <EmptyState
              icon={<Scale />}
              title="Не удалось загрузить сравнение"
              action={<Button onClick={() => q.refetch()}>Повторить</Button>}
            >
              {(q.error as Error | null)?.message}
            </EmptyState>
          </div>
        ) : current ? (
          <section className="card cmp" aria-label={`Пара ${current.position} из ${d.pairs.length}`}>
            <div className="cmp-head">
              <b>
                Пара {current.position} из {d.pairs.length}
              </b>
              <ol className="cmp-dots" aria-hidden="true">
                {d.pairs.map((p) => (
                  <li key={p.id} className={p.pick ? 'done' : p.id === current.id ? 'now' : ''} />
                ))}
              </ol>
            </div>
            <p className="muted cmp-hint">
              Посмотрите оба ролика со звуком, как смотрел бы покупатель в Reels. Какой из них вы бы
              запустили?
            </p>
            <div className="cmp-pair">
              <Player clip={current.left} label="Левый ролик" />
              <Player clip={current.right} label="Правый ролик" />
            </div>
            <div className="cmp-acts">
              <Button
                variant="primary"
                icon={<ArrowLeft />}
                disabled={busy}
                onClick={() => void choose('left')}
              >
                <span className="hide-m">Лучше левый</span>
                <span className="only-m">Левый</span>
              </Button>
              <Button icon={<Equal />} disabled={busy} onClick={() => void choose('tie')}>
                <span className="hide-m">Одинаково</span>
                <span className="only-m">Равны</span>
              </Button>
              <Button variant="primary" disabled={busy} onClick={() => void choose('right')}>
                <span className="hide-m">Лучше правый</span>
                <span className="only-m">Правый</span> <ArrowRight />
              </Button>
            </div>
            <p className="muted cmp-keys">
              Клавиши: ← левый, → правый, ↓ одинаково. Решено {decided} из {d.pairs.length}.
            </p>
          </section>
        ) : (
          d.result && (
            <Card title="Итог сравнения">
              <div className="cmp-result">
                <b className="tnum">
                  Новые не хуже прежних в {d.result.notWorse} из {d.result.total}
                </b>
                {d.result.notWorse * 3 >= d.result.total * 2 ? (
                  <Chip tone="success" dot>
                    Студия не хуже старой
                  </Chip>
                ) : (
                  <Chip tone="warning" dot>
                    Старые пока сильнее
                  </Chip>
                )}
              </div>
              <p className="muted" style={{ margin: '6px 0 14px', fontSize: 'var(--fs-sm)' }}>
                Новый лучше — {d.result.newWins}, одинаково — {d.result.ties}, прежний лучше —{' '}
                {d.result.refWins}. Скажите агенту в чате, что понравилось и что нет, — он учтёт в следующем
                батче.
              </p>
              <ul className="cmp-list">
                {d.pairs.map((p) => {
                  const fresh = p.newSide === 'left' ? p.left : p.right;
                  const old = p.newSide === 'left' ? p.right : p.left;
                  const won = p.pick === 'tie' ? 'tie' : p.pick === p.newSide ? 'new' : 'old';
                  return (
                    <li key={p.id}>
                      <span className="tnum">{p.position}.</span>
                      <span className="min-w-0">
                        новый <b>{fresh.name}</b> против <b>{old.name}</b> · вы выбрали{' '}
                        {p.pick ? PICK_RU[p.pick] : '—'}
                      </span>
                      <Chip tone={won === 'new' ? 'success' : won === 'tie' ? 'default' : 'warning'}>
                        {won === 'new' ? 'новый лучше' : won === 'tie' ? 'одинаково' : 'прежний лучше'}
                      </Chip>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )
        )}
      </div>
    </>
  );
}
